<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { BaseAvatar, BaseBadge, BaseLoader } from '@magma-app/magma-lapilli';
import TheFooter from '@/components/shell/TheFooter.vue';
import SharingIntroStep from './steps/SharingIntroStep.vue';
import SharingFormStep from './steps/SharingFormStep.vue';
import SharingRedirectStep from './steps/SharingRedirectStep.vue';

import AnalyticsHelper from '@/helpers/analytics';
import { useConvertHexToRgb } from '@/composables/useConvertHexToRgb';
import { useAuthStore } from '@/stores/auth';
import { useSharingSteps } from '@/composables/helpee-sharing/useSharingSteps';
import { useSharingForm } from '@/composables/helpee-sharing/useSharingForm';
import { useSharingValidation } from '@/composables/helpee-sharing/useSharingValidation';
import { buildSharingPayload } from '@/composables/helpee-sharing/useSharingPayload';
import { useAsyncAction } from '@/composables/useAsyncAction';

const route = useRoute();
const { t, locale } = useI18n({ useScope: 'global' });
const authStore = useAuthStore();
const { hexColor, rgbColor } = useConvertHexToRgb();

const initialized = ref(false);

const {
 goToStep, isIntro, isCampaigns, isForm, isRedirect, 
} =
  useSharingSteps();

const {
  formData,
  disableBtn,
  duplicateHelpeeMsg,
  resetForm,
  resetCampaignFields,
  clearDuplicateState,
} = useSharingForm();

const { isRunning: isSubmitting, run: runSignup } = useAsyncAction();

const campaigns = computed(
  () => authStore.referredSharingSignUpData?.campaigns || []
);

const needsCampaignSelection = computed(() => {
  return (
    Boolean(authStore.referredSharingSignUpData?.needPickUpCampaign) &&
    campaigns.value.length > 1
  );
});

const helper = computed(() => authStore.referredSharingSignUpData?.helper);
const organization = computed(
  () => authStore.referredSharingSignUpData?.organization
);
const rgbColorValue = computed(() => rgbColor.value || '');

const campaignsOptions = computed(() =>
  campaigns.value.map((campaignOption: { name: string; uuid: string }) => ({
    label: campaignOption.name,
    value: campaignOption.uuid,
  }))
);

const currentCampaign = computed(() => {
  if (needsCampaignSelection.value && formData.selectedCampaign) {
    return campaigns.value.find(
      (campaign) => campaign.uuid === formData.selectedCampaign
    );
  }

  return campaigns.value[0];
});

const criteriaOptions = computed(() => {
  if (!currentCampaign.value?.strongCriteria?.options) {
    return [];
  }

  return currentCampaign.value.strongCriteria.options.map(
    (criteriaOption: { key: string; value: string }) => ({
      label: criteriaOption.value,
      value: criteriaOption.key,
    })
  );
});

const availableTouchpoints = computed(() => {
  return (
    currentCampaign.value?.touchPoints?.map(
      (touchpoint: {
        icon: string;
        name: string;
        id: number;
        type: string;
      }) => ({
        label: touchpoint.name,
        icon: touchpoint.icon,
        value: touchpoint.id,
        type: touchpoint.type,
      })
    ) || []
  );
});

const desiredTouchpoints = computed(() => {
  return availableTouchpoints.value.filter((touchpoint) =>
    formData.signUp.chosenTouchpoints.includes(touchpoint.value)
  );
});

const { v$ } = useSharingValidation({
  formData,
  t,
  needsCampaignSelection,
  availableTouchpoints,
});

const helperTouchpoints = computed(
  () => authStore.referredSharingSignUpData?.helper?.touchPoints || []
);

watch(
  () => formData.signUp.email,
  () => {
    if (disableBtn.value) {
      clearDuplicateState();
    }
  }
);

watch(
  () => [campaigns.value.length, needsCampaignSelection.value],
  () => {
    if (!needsCampaignSelection.value && campaigns.value[0]) {
      formData.selectedCampaign = campaigns.value[0].uuid;
      return;
    }

    if (needsCampaignSelection.value) {
      const hasSelection = campaigns.value.some(
        (campaign) => campaign.uuid === formData.selectedCampaign
      );

      if (!hasSelection) {
        formData.selectedCampaign = null;
      }
    }
  },
  { immediate: true }
);

const onCloseForm = () => {
  goToStep('intro');
  clearDuplicateState();
  resetForm();
  v$.value.$reset();
};

const onGetIntro = () => {
  if (needsCampaignSelection.value) {
    goToStep('campaigns');
  } else {
    if (campaigns.value[0]) {
      formData.selectedCampaign = campaigns.value[0].uuid;
    }

    goToStep('form');
  }

  AnalyticsHelper.trackEvent('ask_introduction', {
    user_properties: 'helpee',
  });
};

const onSelectCampaign = () => {
  resetCampaignFields();
  v$.value.signUp.criteria.$reset();
  goToStep('form');
};

const onHelpeeSignup = () => runSignup(async () => {
  if (disableBtn.value) return;
  try {
    v$.value.signUp.$touch();

    if (!(await v$.value.signUp.$validate())) {
      return;
    }

    const helperId = authStore.referredSharingSignUpData?.helper.id;
    const shareId = route?.params.shareId as string | undefined;

    if (!helperId || !shareId || !currentCampaign.value) {
      return;
    }

    const payload = buildSharingPayload({
      formData,
      campaign: currentCampaign.value,
      helperId,
      shareId,
      desiredTouchpoints: desiredTouchpoints.value,
    });

    if (!payload) {
      return;
    }

    disableBtn.value = true;

    await authStore.sendReferredSharingSignUpData(payload);

    disableBtn.value = false;
    goToStep('redirect');

    AnalyticsHelper.trackEvent('send_intro_request', {
      user_properties: 'helpee',
    });

    setTimeout(() => {
      if (authStore.referredSharingRelationUrl) {
        location.href = authStore.referredSharingRelationUrl;
      }
    }, 5000);
  } catch (error: any) {
    if (error?.response?.data?.message?.includes('user already exist')) {
      disableBtn.value = true;
      duplicateHelpeeMsg.value = t('generic.duplicateHelpee');
    } else {
      console.error(error);
    }
  }
});

onMounted(async () => {
  const shareId = route?.params.shareId;
  if (typeof shareId === 'string') {
    await authStore.getReferredSharingSignUpData(shareId);
  }

  if (organization.value?.languages) {
    locale.value = organization.value.languages[0];
  }

  if (organization.value?.primaryColor) {
    hexColor.value = organization.value.primaryColor;
  }

  initialized.value = true;
});

onUnmounted(() => {
  resetForm();
  v$.value.$reset();
});
</script>

<template>
  <div
    class="h-full bg-background-subtle-hover px-4 py-6 shadow-[0px_4px_8px_-2px_rgba(28,31,39,0.01),0px_2px_4px_-2px_rgba(28,31,39,0.06)] sm:px-8 sm:py-10"
  >
    <template v-if="initialized">
      <div
        class="mx-auto flex w-full max-w-xl rounded-2xl bg-background-default p-5 md:px-10"
      >
        <BaseAvatar
          v-if="organization?.logoUrl"
          :src="organization.logoUrl"
          :alt="`${$t('generic.profilePicture')} ${organization.name}`"
          :fallback="organization.name.charAt(0)"
          size="md"
        />

        <h1 class="ml-4">
          <span
            class="block text-mgm-txt-sm font-normal text-foreground-default"
          >
            {{ $t('helpeeSharingView.certified') }}
          </span>

          <span
            class="block text-mgm-txt-md font-medium text-foreground-emphasis"
          >
            {{ organization?.name }}
          </span>
        </h1>
      </div>

      <div
        class="relative mx-auto my-4 w-full max-w-xl rounded-2xl bg-background-default pb-5 shadow-[0px_4px_8px_-2px_rgba(28,31,39,0.01),0px_2px_4px_-2px_rgba(28,31,39,0.06)] md:pb-10"
      >
        <SharingIntroStep
          v-if="isIntro"
          :helper="helper"
          :organization="organization"
          :rgb-color="rgbColorValue"
          @start="onGetIntro"
        />

        <SharingFormStep
          v-else-if="isCampaigns || isForm"
          :form-data="formData"
          :campaigns-options="campaignsOptions"
          :criteria-options="criteriaOptions"
          :available-touchpoints="availableTouchpoints"
          :desired-touchpoints="desiredTouchpoints"
          :strong-criteria="currentCampaign?.strongCriteria"
          :show-campaign-select="needsCampaignSelection"
          :show-form="isForm || !needsCampaignSelection"
          :validation="v$"
          :duplicate-helpee-msg="duplicateHelpeeMsg"
          :disable-btn="disableBtn"
          :primary-color="organization?.primaryColor"
          :rgb-color="rgbColorValue"
          :helper="helper"
          @close="onCloseForm"
          @select-campaign="onSelectCampaign"
          @submit="onHelpeeSignup"
        />

        <SharingRedirectStep
          v-else-if="isRedirect"
          :helper="helper"
          :organization-name="organization?.name"
          :firstname="formData.signUp.firstname"
        />
      </div>

      <div
        v-if="isIntro"
        class="mx-auto w-full max-w-xl rounded-2xl bg-background-default px-5 py-5 shadow-[0px_4px_8px_-2px_rgba(28,31,39,0.01),0px_2px_4px_-2px_rgba(28,31,39,0.06)] md:px-10 md:py-10"
      >
        <BaseBadge
          :style="{
            backgroundColor: `rgba(${rgbColor}, 0.05)`,
            color: organization?.primaryColor
          }"
          :text="$t('helpeeSharingView.touchpoints')"
          size="md"
        />

        <div class="mt-[18px] flex items-center">
          <BaseAvatar
            v-for="(touchpoint, touchpointIndex) in helperTouchpoints"
            :key="touchpointIndex"
            class="mr-4 h-8 w-8 cursor-pointer"
            :src="touchpoint.icon"
            :alt="`${$t('generic.profilePicture')} ${touchpoint.name}`"
            :fallback="touchpoint.name.charAt(0)"
            shape="squared"
            @click="onGetIntro"
          />
        </div>
      </div>

      <TheFooter />
    </template>

    <BaseLoader v-else class="min-h-screen" />
  </div>
</template>
